import { calculateTotalAmountFromInvoiceItemsWithVAT } from "@/composables/money/moneyUtils";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { InvoiceItem, MonthSplit } from "@/types";
import { computed, ref } from "vue";

type PendingInvoiceItemsResponse = {
  invoice_items: InvoiceItem[];
  default_invoice_number: string;
  month_split: MonthSplit | null;
};

/**
 * Holds a company's invoice items still open for settlement, i.e. what Soldera owes the company
 * net of fees, as served by `GET /api/company/{id}/pending-invoice-items`.
 */
export const usePendingInvoiceItems = () => {
  const pendingInvoiceItems = ref<InvoiceItem[]>([]);
  const defaultInvoiceNumber = ref("");
  const monthSplit = ref<MonthSplit | null>(null);

  const pendingPayout = computed(() =>
    calculateTotalAmountFromInvoiceItemsWithVAT(pendingInvoiceItems.value)
  );
  // A fee-only or net-negative balance is settled against sales, not invoiced
  const canSubmitInvoice = computed(() => pendingPayout.value > 0);

  const clearPendingInvoiceItems = () => {
    pendingInvoiceItems.value = [];
    monthSplit.value = null;
  };

  /**
   * Loads the company's pending items and default invoice number. The previous company's items
   * are dropped first, so a failed fetch never leaves them on screen under another company.
   *
   * @throws The API error, for the caller to hand to the global error handler
   */
  const fetchPendingInvoiceItems = async (companyId: number) => {
    clearPendingInvoiceItems();
    defaultInvoiceNumber.value = "";
    const response = await goSolidApi.get<PendingInvoiceItemsResponse>(
      `/api/company/${companyId}/pending-invoice-items`
    );
    pendingInvoiceItems.value = response.data.invoice_items;
    defaultInvoiceNumber.value = response.data.default_invoice_number;
    monthSplit.value = response.data.month_split;
  };

  return {
    pendingInvoiceItems,
    defaultInvoiceNumber,
    monthSplit,
    pendingPayout,
    canSubmitInvoice,
    fetchPendingInvoiceItems,
    clearPendingInvoiceItems,
  };
};
